import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/Footer';
import axiosInstance from '../utils/axios';
import {
  Card, Row, Col, Statistic, Button, Space, Table, Tag, Spin, Alert,
  Tooltip, Modal, Checkbox, Divider, DatePicker, message as antMessage,
} from 'antd';
import {
  HomeOutlined, BarChartOutlined, UserOutlined, CarOutlined,
  FileProtectOutlined, WifiOutlined, TeamOutlined, WarningOutlined,
  FileExcelOutlined, PrinterOutlined, SettingOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { hssColors, commonStyles } from '../config/theme';

const { RangePicker } = DatePicker;


const COLORES_TIPO = ['#3498db', '#e74c3c', '#f39c12', '#2ecc71', '#9b59b6'];
const COLORES_SERVICIO = { RASTREO: '#3498db', VISION: '#8e44ad', RASTREO_VISION: '#16a085' };

const SectionTitle = ({ icon, children, color = hssColors.primary }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, marginTop: 8 }}>
    <span style={{ fontSize: 22, color }}>{icon}</span>
    <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: hssColors.textPrimary }}>{children}</h2>
  </div>
);

const DashboardGerencial = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalExport, setModalExport] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [seleccion, setSeleccion] = useState(['clientes', 'vehiculos', 'gps', 'sinContrato', 'usuariosAcceso']);
  const [rangoFechas, setRangoFechas] = useState(null);

  const OPCIONES = [
    { value: 'clientes',      label: `Clientes`,             desc: `${data?.clientes?.total ?? 0} registros — CI, nombre, contacto, vehículos`,     grupo: 'Datos maestros' },
    { value: 'vehiculos',     label: `Vehículos`,            desc: `${data?.vehiculos?.total ?? 0} registros — chapa, tipo, servicio, GPS asignado`,   grupo: 'Datos maestros' },
    { value: 'gps',           label: `GPS — Inventario`,     desc: `${data?.gps?.total ?? 0} dispositivos — IMEI, línea SIM, estado, vehículo`,        grupo: 'Datos maestros' },
    { value: 'usuariosAcceso',label: `Usuarios Acceso`,      desc: `${data?.usuarios_clientes?.total ?? 0} accesos al sistema de rastreo satelital`,   grupo: 'Datos maestros' },
    { value: 'sinContrato',   label: `Sin Contrato`,         desc: `${data?.contratos?.clientes_sin_contrato?.length ?? 0} clientes sin contrato firmado`, grupo: 'Contratos' },
    { value: 'ordenesMes',    label: `Órdenes por mes`,      desc: 'Histórico mensual por tipo (instalación, mantenimiento, etc.)',                    grupo: 'Operaciones', conFecha: true },
    { value: 'tecnicos',      label: `Rendimiento técnicos`, desc: 'Total de órdenes y % de efectividad por técnico',                                  grupo: 'Operaciones', conFecha: true },
  ];

  const ejecutarExport = async () => {
    if (seleccion.length === 0) {
      antMessage.warning('Seleccioná al menos una sección para exportar.');
      return;
    }
    setExportando(true);
    try {
      const fetchs = {};
      if (seleccion.includes('clientes'))       fetchs.clientes       = axiosInstance.get('/clientes');
      if (seleccion.includes('vehiculos'))      fetchs.vehiculos      = axiosInstance.get('/vehiculos');
      if (seleccion.includes('gps'))            fetchs.gps            = axiosInstance.get('/gps');
      if (seleccion.includes('usuariosAcceso')) fetchs.usuariosAcceso = axiosInstance.get('/usuarios-clientes');

      const results = {};
      await Promise.all(Object.entries(fetchs).map(async ([k, p]) => { results[k] = (await p).data; }));

      const wb = XLSX.utils.book_new();
      const fecha = new Date().toLocaleDateString('es-PY');
      const [desde, hasta] = rangoFechas || [null, null];

      // ── Resumen siempre incluido ─────────────────────────────────────
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([
        ['HSS Rastreo Satelital — Reporte General', '', fecha],
        ['Secciones exportadas:', seleccion.join(', ')],
        ...(desde ? [['Período:', `${desde.format('DD/MM/YYYY')} al ${hasta.format('DD/MM/YYYY')}`]] : []),
        [],
        ['CLIENTES', data.clientes.total],
        ['Con vehículos', data.clientes.con_vehiculos],
        ['Sin vehículos', data.clientes.sin_vehiculos],
        [],
        ['VEHÍCULOS', data.vehiculos.total],
        ...data.vehiculos.por_tipo.map(r => [r.tipo, r.cantidad]),
        [],
        ['GPS', data.gps.total],
        ['En stock', data.gps.stock], ['Instalados', data.gps.instalado], ['De baja', data.gps.baja],
        [],
        ['ÓRDENES', data.ordenes.total],
        ['Activas', data.ordenes.activas], ['Completadas', data.ordenes.completadas], ['Canceladas', data.ordenes.canceladas],
        [],
        ['CONTRATOS', data.contratos.total],
        ['Firmados', data.contratos.firmados], ['Pendientes', data.contratos.pendientes],
      ]), 'Resumen General');

      // ── Clientes ─────────────────────────────────────────────────────
      if (results.clientes?.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
          results.clientes.map(c => ({
            'DNI': c.dni, 'Nombre': c.nombre, 'Apellido': c.apellido,
            'RUC': c.ruc || '', 'Email': c.email || '', 'Celular': c.celular || '',
            'Domicilio': c.domicilio || '',
            'Total Vehículos': c.total_vehiculos,
            'Veh. Rastreo': c.vehiculos_rastreo, 'Veh. Vision': c.vehiculos_vision,
          }))
        ), `Clientes (${results.clientes.length})`);
      }

      // ── Vehículos ────────────────────────────────────────────────────
      if (results.vehiculos?.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
          results.vehiculos.map(v => ({
            'Chapa': v.chapa, 'Marca': v.marca, 'Modelo': v.modelo,
            'Año': v.ano || '', 'Color': v.color || '',
            'Tipo': v.tipo || '', 'Servicio': v.servicio || '',
            'GPS IMEI': v.gps || '', 'Cliente DNI': v.cliente_dni || '',
          }))
        ), `Vehículos (${results.vehiculos.length})`);
      }

      // ── GPS ──────────────────────────────────────────────────────────
      if (results.gps?.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
          results.gps.map(g => ({
            'Marca': g.marca, 'Modelo': g.modelo, 'IMEI': g.imei,
            'N° Línea': g.numero_linea || '', 'Estado': g.estado,
            'Vehículo': g.vehiculo || '', 'Chapa': g.vehiculo_chapa || '',
          }))
        ), `GPS (${results.gps.length})`);
      }

      // ── Sin Contrato ─────────────────────────────────────────────────
      if (seleccion.includes('sinContrato') && data.contratos.clientes_sin_contrato.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
          data.contratos.clientes_sin_contrato.map(r => ({
            'DNI': r.dni, 'Nombre': r.nombre, 'Apellido': r.apellido,
          }))
        ), `Sin Contrato (${data.contratos.clientes_sin_contrato.length})`);
      }

      // ── Usuarios Acceso ──────────────────────────────────────────────
      if (results.usuariosAcceso?.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
          results.usuariosAcceso.map(u => ({
            'Usuario': u.usuario,
            'Cliente': u.cliente_nombre || 'Sin asignar',
            'Vehículos': u.vehiculos.map(v => v.chapa).join(', '),
            'Observaciones': u.observaciones || '',
          }))
        ), `Usuarios Acceso (${results.usuariosAcceso.length})`);
      }

      // ── Órdenes por mes (con filtro de fecha) ────────────────────────
      if (seleccion.includes('ordenesMes')) {
        const meses = desde
          ? data.ordenes.por_mes.filter(r => {
              const [mes, anio] = r.label.split('/').map(Number);
              const d = new Date(anio, mes - 1, 1);
              return d >= desde.toDate() && d <= hasta.toDate();
            })
          : data.ordenes.por_mes;
        if (meses.length > 0) {
          XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
            meses.map(r => ({
              'Mes': r.label, 'Total': r.total,
              'Instalaciones': r.instalaciones,
              'Desinstalaciones': r.desinstalaciones,
              'Mantenimientos': r.mantenimientos,
            }))
          ), 'Órdenes por Mes');
        }
      }

      // ── Técnicos ─────────────────────────────────────────────────────
      if (seleccion.includes('tecnicos') && data.ordenes.por_tecnico.length > 0) {
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
          data.ordenes.por_tecnico.map(r => ({
            'Técnico': r.nombre,
            'Total órdenes': r.total,
            'Completadas': r.completadas,
            '% Efectividad': r.total > 0 ? Math.round((r.completadas / r.total) * 100) + '%' : '0%',
          }))
        ), 'Técnicos');
      }

      const nombreArchivo = [
        'HSS',
        seleccion.length === OPCIONES.length ? 'Reporte_Completo' : `Reporte_${seleccion.map(s => OPCIONES.find(o => o.value === s)?.label.split(' ')[0]).join('_')}`,
        desde ? `${desde.format('YYYYMM')}-${hasta.format('YYYYMM')}` : new Date().toISOString().slice(0, 10),
      ].join('_') + '.xlsx';

      const buf = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(new Blob([buf], { type: 'application/octet-stream' }), nombreArchivo);
      setModalExport(false);
    } catch {
      antMessage.error('Error al exportar. Intente de nuevo.');
    } finally {
      setExportando(false);
    }
  };

  useEffect(() => {
    axiosInstance.get('/reportes')
      .then(r => setData(r.data))
      .catch(() => setError('No se pudo cargar los reportes'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Spin size="large" tip="Cargando reportes..." />
    </div>
  );

  if (error) return (
    <div style={{ padding: 40 }}>
      <Alert type="error" message={error} />
    </div>
  );

  const { clientes, vehiculos, gps, ordenes, contratos, usuarios_clientes } = data;

  const columnasSinContrato = [
    { title: 'DNI', dataIndex: 'dni', key: 'dni', width: 100 },
    { title: 'Nombre', key: 'nombre', render: (_, r) => `${r.nombre} ${r.apellido}` },
    {
      title: '', key: 'ir', width: 80,
      render: (_, r) => (
        <Button size="small" type="link" onClick={() => navigate(`/clientes/${r.dni}`)}>
          Ver
        </Button>
      ),
    },
  ];

  const columnasTecnicos = [
    { title: 'Técnico', dataIndex: 'nombre', key: 'nombre', render: n => <strong>{n}</strong> },
    { title: 'Total órdenes', dataIndex: 'total', key: 'total', align: 'center' },
    { title: 'Completadas', dataIndex: 'completadas', key: 'completadas', align: 'center',
      render: v => <Tag color="green">{v}</Tag> },
    { title: '% Efectividad', key: 'pct', align: 'center',
      render: (_, r) => {
        const pct = r.total > 0 ? Math.round((r.completadas / r.total) * 100) : 0;
        return <Tag color={pct >= 80 ? 'green' : pct >= 50 ? 'orange' : 'red'}>{pct}%</Tag>;
      }
    },
  ];

  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      <style>{`
        @media print {
          header, nav, button, .ant-btn, footer { display: none !important; }
          body { background: white !important; }
          .ant-card { box-shadow: none !important; border: 1px solid #ddd !important; }
          .recharts-wrapper { page-break-inside: avoid; }
        }
      `}</style>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, boxShadow: commonStyles.shadowMd, position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ padding: '16px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <img src="/logo-hss.png" alt="HSS" style={{ height: 45, cursor: 'pointer' }} onClick={() => navigate('/dashboard')} />
            <div style={{ borderLeft: `2px solid ${hssColors.gray200}`, height: 40 }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Dashboard Gerencial</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Estadísticas y análisis del negocio</p>
            </div>
          </div>
          <Space>
            <Button
              icon={<FileExcelOutlined />}
              style={{ borderRadius: 8, background: '#1D6F42', color: '#fff', borderColor: '#1D6F42', fontWeight: 600 }}
              size="large"
              onClick={() => setModalExport(true)}
            >
              Exportar Excel
            </Button>
            <Tooltip title="Imprimir / Guardar como PDF">
              <Button
                icon={<PrinterOutlined />}
                onClick={() => window.print()}
                style={{ borderRadius: 8, fontWeight: 600 }}
                size="large"
              >
                Imprimir / PDF
              </Button>
            </Tooltip>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large"
              style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Volver
            </Button>
          </Space>
        </div>
      </div>

      <div style={{ padding: '24px 32px', flex: 1 }}>

        {/* ── KPIs generales ──────────────────────────────────────────────── */}
        <SectionTitle icon={<BarChartOutlined />} color={hssColors.primary}>Resumen General</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          {[
            { title: 'Clientes', value: clientes.total, color: '#3498db', icon: <UserOutlined /> },
            { title: 'Vehículos', value: vehiculos.total, color: '#e74c3c', icon: <CarOutlined /> },
            { title: 'Órdenes totales', value: ordenes.total, color: '#f39c12', icon: <BarChartOutlined /> },
            { title: 'Contratos firmados', value: contratos.firmados, color: '#27ae60', icon: <FileProtectOutlined /> },
            { title: 'GPS instalados', value: gps.instalado, color: '#16a085', icon: <WifiOutlined /> },
            { title: 'Usuarios de clientes', value: usuarios_clientes.total, color: '#8e44ad', icon: <TeamOutlined /> },
          ].map(s => (
            <Col xs={12} sm={8} lg={4} key={s.title}>
              <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, borderTop: `4px solid ${s.color}` }}>
                <Statistic
                  title={<span style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.title}</span>}
                  value={s.value}
                  prefix={<span style={{ color: s.color }}>{s.icon}</span>}
                  valueStyle={{ color: s.color, fontWeight: 700, fontSize: 28 }}
                />
              </Card>
            </Col>
          ))}
        </Row>

        {/* ── Clientes ─────────────────────────────────────────────────────── */}
        <SectionTitle icon={<UserOutlined />} color="#3498db">Clientes</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={8}>
            <Card title="Con / Sin vehículos" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={[
                    { name: 'Con vehículos', value: clientes.con_vehiculos },
                    { name: 'Sin vehículos', value: clientes.sin_vehiculos },
                  ]} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    <Cell fill="#3498db" />
                    <Cell fill="#dfe6e9" />
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title={<Space><WarningOutlined style={{ color: '#e74c3c' }} /><span>Clientes sin contrato firmado ({contratos.clientes_sin_contrato.length})</span></Space>}
              style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <Table
                columns={columnasSinContrato}
                dataSource={contratos.clientes_sin_contrato}
                rowKey="dni"
                size="small"
                pagination={{ pageSize: 6, showSizeChanger: false, showTotal: t => `${t} clientes` }}
                scroll={{ y: 180 }}
              />
            </Card>
          </Col>
        </Row>

        {/* ── Vehículos ─────────────────────────────────────────────────────── */}
        <SectionTitle icon={<CarOutlined />} color="#e74c3c">Vehículos</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={12}>
            <Card title="Por tipo" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={vehiculos.por_tipo} margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tipo" />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar dataKey="cantidad" name="Vehículos" radius={[4, 4, 0, 0]}>
                    {vehiculos.por_tipo.map((_, i) => <Cell key={i} fill={COLORES_TIPO[i % COLORES_TIPO.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={12}>
            <Card title="Por servicio" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={vehiculos.por_servicio} cx="50%" cy="50%" outerRadius={80}
                    dataKey="cantidad" nameKey="servicio"
                    label={({ servicio, cantidad }) => `${servicio}: ${cantidad}`}>
                    {vehiculos.por_servicio.map((entry, i) => (
                      <Cell key={i} fill={COLORES_SERVICIO[entry.servicio] || COLORES_TIPO[i]} />
                    ))}
                  </Pie>
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

        {/* ── Órdenes de trabajo ────────────────────────────────────────────── */}
        <SectionTitle icon={<BarChartOutlined />} color="#f39c12">Órdenes de Trabajo</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={8}>
            <Card title="Por estado" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={[
                    { name: 'Activas', value: ordenes.activas },
                    { name: 'Completadas', value: ordenes.completadas },
                    { name: 'Canceladas', value: ordenes.canceladas },
                  ]} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                    <Cell fill="#f39c12" />
                    <Cell fill="#27ae60" />
                    <Cell fill="#e74c3c" />
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={16}>
            <Card title="Por mes (histórico)" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={ordenes.por_mes} margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Legend />
                  <Bar dataKey="instalaciones" name="Instalaciones" fill="#3498db" stackId="a" radius={[0,0,0,0]} />
                  <Bar dataKey="desinstalaciones" name="Desinstalaciones" fill="#e74c3c" stackId="a" />
                  <Bar dataKey="mantenimientos" name="Mantenimientos" fill="#f39c12" stackId="a" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24}>
            <Card title="Rendimiento por técnico" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <Table
                columns={columnasTecnicos}
                dataSource={ordenes.por_tecnico}
                rowKey="nombre"
                size="small"
                pagination={false}
              />
            </Card>
          </Col>
        </Row>

        {/* ── GPS ──────────────────────────────────────────────────────────── */}
        <SectionTitle icon={<WifiOutlined />} color="#16a085">Inventario GPS</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={10}>
            <Card title="Estado del inventario" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={[
                    { name: 'En stock', value: gps.stock },
                    { name: 'Instalados', value: gps.instalado },
                    { name: 'De baja', value: gps.baja },
                  ]} cx="50%" cy="50%" outerRadius={80} dataKey="value"
                    label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}>
                    <Cell fill="#27ae60" />
                    <Cell fill="#3498db" />
                    <Cell fill="#95a5a6" />
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <Card style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd, height: '100%' }}
              styles={{ body: { display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 24, padding: 32 } }}>
              {[
                { label: 'Total dispositivos', value: gps.total, color: hssColors.primary },
                { label: 'En stock (disponibles)', value: gps.stock, color: '#27ae60' },
                { label: 'Instalados en vehículos', value: gps.instalado, color: '#3498db' },
                { label: 'Dados de baja', value: gps.baja, color: '#95a5a6' },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 15, color: hssColors.textSecondary }}>{s.label}</span>
                  <span style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.value}</span>
                </div>
              ))}
            </Card>
          </Col>
        </Row>

        {/* ── Contratos ─────────────────────────────────────────────────────── */}
        <SectionTitle icon={<FileProtectOutlined />} color="#27ae60">Contratos</SectionTitle>
        <Row gutter={[16, 16]} style={{ marginBottom: 32 }}>
          <Col xs={24} lg={10}>
            <Card title="Estado" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={[
                    { name: 'Firmados', value: contratos.firmados },
                    { name: 'Pendientes', value: contratos.pendientes },
                  ]} cx="50%" cy="50%" outerRadius={75} dataKey="value"
                    label={({ name, value }) => `${name}: ${value}`}>
                    <Cell fill="#27ae60" />
                    <Cell fill="#f39c12" />
                  </Pie>
                  <RechartsTooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card>
          </Col>
          <Col xs={24} lg={14}>
            <Card title="Por tipo de servicio" style={{ borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={contratos.por_tipo} margin={{ top: 0, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tipo" />
                  <YAxis allowDecimals={false} />
                  <RechartsTooltip />
                  <Bar dataKey="cantidad" name="Contratos" radius={[4, 4, 0, 0]}>
                    {contratos.por_tipo.map((_, i) => <Cell key={i} fill={COLORES_TIPO[i % COLORES_TIPO.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </Col>
        </Row>

      </div>

      {/* ── Modal de exportación ──────────────────────────────────────── */}
      <Modal
        title={<Space><FileExcelOutlined style={{ color: '#1D6F42' }} /><span>Configurar exportación Excel</span></Space>}
        open={modalExport}
        onCancel={() => setModalExport(false)}
        width={520}
        footer={[
          <Button key="cancel" onClick={() => setModalExport(false)}>Cancelar</Button>,
          <Button
            key="export"
            type="primary"
            icon={<FileExcelOutlined />}
            style={{ background: '#1D6F42', borderColor: '#1D6F42' }}
            loading={exportando}
            onClick={ejecutarExport}
          >
            Exportar seleccionado
          </Button>,
        ]}
      >
        <p style={{ color: '#555', marginBottom: 16 }}>
          Seleccioná qué información incluir en el archivo. Cada sección se exporta como una hoja separada.
        </p>

        {['Datos maestros', 'Contratos', 'Operaciones'].map(grupo => {
          const ops = OPCIONES.filter(o => o.grupo === grupo);
          const conFecha = ops.some(o => o.conFecha);
          return (
            <div key={grupo}>
              <Divider orientation="left" style={{ fontSize: 13, color: '#888', margin: '12px 0 8px' }}>{grupo}</Divider>
              {conFecha && (
                <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>Filtrar por fecha:</span>
                  <RangePicker
                    picker="month"
                    format="MM/YYYY"
                    placeholder={['Desde', 'Hasta']}
                    onChange={setRangoFechas}
                    style={{ flex: 1 }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ops.map(op => (
                  <div
                    key={op.value}
                    onClick={() => setSeleccion(prev =>
                      prev.includes(op.value) ? prev.filter(v => v !== op.value) : [...prev, op.value]
                    )}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px',
                      borderRadius: 8, border: `1.5px solid ${seleccion.includes(op.value) ? '#1D6F42' : '#e8e8e8'}`,
                      background: seleccion.includes(op.value) ? '#f0faf4' : '#fafafa',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    <Checkbox checked={seleccion.includes(op.value)} style={{ marginTop: 2 }} />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{op.label}</div>
                      <div style={{ fontSize: 12, color: '#888' }}>{op.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        <div style={{ marginTop: 14, padding: '8px 12px', background: '#f5f5f5', borderRadius: 8, fontSize: 12, color: '#888' }}>
          El <b>Resumen General</b> (KPIs del sistema) siempre se incluye como primera hoja.
        </div>
      </Modal>

      <Footer />
    </div>
  );
};

export default DashboardGerencial;
